import { faCheck, faPlus, faTrash, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Node, Handle, NodeProps, Position } from "@xyflow/react";
import { FunctionComponent, useMemo, useState } from "react";

import { t } from "i18next";

import { format } from "date-fns";

import { Icon, Modal } from "@/components";

import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";

import { classNames } from "@/utils/classNames";
import { toPriorityFilterItems } from "@/utils/priorityFilterOptions";

import { Information } from "@/components/Information/Information";

import { Radio } from "@/components/Radio/Radio";

import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";
import { PriorityFilterStatus } from "@/enum/list.enum";

import { NodeData } from "../../pages/CampaignPage";
import { conditionTimingInfo } from "../../utils/conditionGateInfo";
import { SentDetailsTooltip } from "./SentDetailsTooltip";

export type ConditionalNodeConfig = Node<NodeData, "ConditionalNode">;

export const ConditionalNode: FunctionComponent<NodeProps<ConditionalNodeConfig>> = (props) => {
	const { data, selected } = props;
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const childrenNodes = data.getChildrenNodes?.(props.id) || [];

	const hasNextNode = useMemo(() => {
		return childrenNodes.length >= 1 || false;
	}, [childrenNodes]);

	const hasNextYesNode = useMemo(() => {
		return (
			!!childrenNodes.find((node) => (node?.data?.edgeType as unknown as string) === "yesEdge") ||
			false
		);
	}, [childrenNodes]);

	const hasNextNoNode = useMemo(() => {
		return (
			!!childrenNodes.find((node) => (node?.data?.edgeType as unknown as string) === "noEdge") ||
			false
		);
	}, [childrenNodes]);

	const conditionalValue = useMemo(() => {
		return data.conditionalValue;
	}, [data.conditionalValue]);

	return (
		<div
			className={classNames(
				"rounded-lg border bg-white h-auto min-h-[50px] relative w-[350px]",
				data.isDisconnectedTarget ? "border-status-error border-2" : "",
				data.alreadySent && !data.isDisconnectedTarget ? "border-status-success" : "",
				data.scheduledFor && !data.isDisconnectedTarget ? "border-status-warning" : "",
				!data.isDisconnectedTarget && selected ? "border-blue-action" : "",
				!data.isDisconnectedTarget && !selected ? "border-gray-200" : ""
			)}
		>
			<div className="text-sm rounded-t-lg border-b border-gray-200 h-auto w-full bg-action-200 text-text-meta px-2  py-1 flex flex-col gap-1">
				<div className="flex flex-row justify-between items-center">
					<span className="text-sm">Step {data.level} - Condition</span>

					<div className="flex items-center justify-center gap-2">
						{childrenNodes?.length >= 1 ? (
							<Icon
								className="w-6 h-6 border border-status-success text-status-success bg-status-success-light rounded-full p-1"
								icon={faCheck}
							/>
						) : (
							<Icon
								className="w-6 h-6 border border-status-error text-status-error bg-status-error-light rounded-full p-1"
								icon={faXmark}
							/>
						)}
						{!hasNextNode && data.deleteNode && !data.alreadySent && (
							<div
								className="flex items-center justify-center cursor-pointer"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setShowDeleteModal(true);
								}}
							>
								<Icon
									className="w-6 h-6 border border-status-error text-status-error bg-status-error-light rounded-full p-1 hover:text-status-error-light hover:bg-status-error"
									icon={faTrash}
								/>
							</div>
						)}
					</div>
				</div>
				{(data.alreadySent || !!data.scheduledFor) && (
					<div className="flex flex-row justify-between">
						<div className="w-auto top-0 right-0 flex items-center justify-center gap-1">
							{data.alreadySent && (
								<SentDetailsTooltip sentAt={data.sentAt} sentByUserName={data.sentByUserName}>
									<div className="text-text-meta text-xs cursor-help">Condition executed</div>
								</SentDetailsTooltip>
							)}

							{!!data.scheduledFor && (
								<div className="text-text-meta text-xs">
									Scheduled for {format(new Date(data.scheduledFor || ""), "HH:mm dd.MM.yyyy")}
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			<div className="relative  grid grid-cols-[39px_270px_39px]">
				{!data.alreadySent && !data.scheduledFor && data?.handleAddStep && (
					<div className="col-span-1">
						<div
							className=" bg-status-success-light h-full flex flex-col items-center justify-center text-sm text-status-success flex z-[100] rounded-bl-lg"
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();

								data?.handleAddStep?.({
									position: { x: props.positionAbsoluteX, y: props.positionAbsoluteY },
									initiatorId: props.id,
									edgeType: "yesEdge",
									nodeType: "conditionalNode",
									hasNextNode,
									hasNextYesNode,
									hasNextNoNode,
								});
							}}
						>
							Yes
							<Icon
								className="w-4 h-4 border border-status-success rounded-full p-0.5 mt-1"
								icon={faPlus}
							/>
						</div>
					</div>
				)}
				<div
					className={classNames(
						"relative",
						data.alreadySent || data.scheduledFor || !data?.handleAddStep
							? "w-full col-span-3"
							: "w-[270px]"
					)}
				>
					<div className="text-sm h-auto  min-h-[50px] w-full text-text-meta px-2  py-1 flex flex-col items-start justify-start gap-2">
						<div className="flex items-center gap-2 w-full">
							<div className="w-full text-sm text-text-meta">{conditionalValue?.property}</div>
						</div>
						{conditionalValue?.property !== "Lead Priority" &&
							!["hasEmail", "hasLinkedin"].includes(conditionalValue?.value || "") && (
								<div className="flex flex-col  gap-2 w-full">
									<div className="flex flex-row items-center  w-full gap-2">
										<Radio
											isChecked={data.waitUntil || false}
											onChange={(e) => {
												e.stopPropagation();
												e.preventDefault();

												data?.handleSetWaitUntil?.(props.id, true);
												data?.handleSetDays?.(props.id, null);
											}}
										>
											Wait until
										</Radio>
										<Radio
											isChecked={!data.waitUntil || false}
											onChange={(e) => {
												e.stopPropagation();
												e.preventDefault();

												data?.handleSetWaitUntil?.(props.id, false);
												// Commit the shown default (1) rather than null — otherwise the
												// "1 day" the user sees is never persisted and the backend adds
												// no delay. Keep any value the user already typed.
												data?.handleSetDays?.(props.id, data.days ?? 1);
											}}
										>
											Within
										</Radio>
										{/* On the radio row rather than beside the day input: the "Wait until" mode has
										    no input to hang it off, and that is the mode whose timing users ask about
										    (PREC-3908). The copy follows the selected mode. */}
										<Information information={conditionTimingInfo(data.waitUntil)} />
									</div>
									{!data.waitUntil && (
										<div className="flex items-center gap-2">
											<InputField
												className="-mb-3"
												handleChange={(e) => {
													if (
														isNaN(Number(e.target.value)) ||
														e.target.value?.length > 2 ||
														Number(e.target.value) < 0 ||
														Number(e.target.value) > 30
													) {
														return;
													}

													data?.handleSetDays?.(props.id, Number(e.target.value));
												}}
												isDisabled={data.alreadySent || !!data.scheduledFor}
												max={30}
												min={0}
												name="days"
												pattern="[0-9]{2}"
												placeholder="days"
												size={ButtonSize.XS}
												type="text"
												value={data.days ?? "1"}
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
												}}
											/>
											<span className="text-text-meta flex gap-1">
												{(data.days || 0) > 1 || !data.days || data.days === 0 ? "days" : "day"}
											</span>{" "}
										</div>
									)}
								</div>
							)}
						{["leadPriority"].includes(conditionalValue?.currentType || "") && (
							<div className="flex flex-col  gap-2 w-full">
								<div className="flex flex-row items-center  w-full gap-2">
									<DropdownWithCheckboxes
										// Locked campaigns can't change step conditions (PREC-3249); the
										// handler is also withheld so changes can't be persisted.
										disabled={!data?.handleSetConditionalValue || data.alreadySent}
										data={toPriorityFilterItems((status) =>
											t(`inbox.priorityFilterOptions.${status}`)
										)}
										defaultValue={
											data.conditionalValue?.value && data.conditionalValue?.value.length > 0
												? data.conditionalValue?.value.split(", ").map((i) => {
														return {
															title: t(`inbox.priorityFilterOptions.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL}`),
															id: PriorityFilterStatus.ALL,
														},
													]
										}
										floating={true}
										handleSelect={(values: DropdownWithCheckboxesItem[]) => {
											const addedValue = values.find(
												(val) =>
													!data.conditionalValue?.value.includes(val.id as PriorityFilterStatus)
											);

											const newValues = values.filter(
												(val) =>
													val.id !== PriorityFilterStatus.ALL &&
													val.id !== PriorityFilterStatus.ALL_WITH_PRIORITY
											);

											if (addedValue && addedValue.id === PriorityFilterStatus.ALL) {
												data?.handleSetConditionalValue?.(props.id, {
													property: conditionalValue?.property || "",
													value: PriorityFilterStatus.ALL,
													title: t(`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL}`),
													currentType: conditionalValue?.currentType || "",
												});
											} else if (
												addedValue &&
												addedValue.id === PriorityFilterStatus.ALL_WITH_PRIORITY
											) {
												data?.handleSetConditionalValue?.(props.id, {
													property: conditionalValue?.property || "",
													value: PriorityFilterStatus.ALL_WITH_PRIORITY,
													title: t(
														`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL_WITH_PRIORITY}`
													),
													currentType: conditionalValue?.currentType || "",
												});
											} else {
												data?.handleSetConditionalValue?.(props.id, {
													property: conditionalValue?.property || "",
													value: newValues.map((v) => v.id as PriorityFilterStatus).join(", "),
													title: newValues
														.map((v) =>
															t(`inbox.priorityFilterOptions.${v.id as PriorityFilterStatus}`)
														)
														.join(", "),
													currentType: conditionalValue?.currentType || "",
												});
											}
										}}
										size={ButtonSize.XS}
										title="Score"
										value={
											data.conditionalValue?.value &&
											data.conditionalValue?.value?.split(", ").length > 0
												? data.conditionalValue?.value.split(", ").map((i) => {
														return {
															title: t(`inbox.priorityFilterOptions.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(
																`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL_WITH_PRIORITY}`
															),
															id: PriorityFilterStatus.ALL_WITH_PRIORITY,
														},
													]
										}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
				{!data.alreadySent && !data.scheduledFor && data?.handleAddStep && (
					<div className={classNames(data.waitUntil ? "hidden" : "")}>
						<div
							className=" bg-status-error-light h-full flex items-center justify-center text-sm text-status-error px-2 flex flex-col z-[100] rounded-br-lg"
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();

								data?.handleAddStep?.({
									position: { x: props.positionAbsoluteX, y: props.positionAbsoluteY },
									initiatorId: props.id,
									edgeType: "noEdge",
									nodeType: "conditionalNode",
									hasNextNode,
									hasNextYesNode,
									hasNextNoNode,
								});
							}}
						>
							No
							<Icon
								className="w-4 h-4 border border-status-error rounded-full p-0.5 mt-1"
								icon={faPlus}
							/>
						</div>
					</div>
				)}

				<div className="z-[100]">
					<Handle
						id="normalEdge"
						position={Position.Top}
						style={
							data.isDisconnectedTarget
								? {
										visibility: "visible",
										width: 12,
										height: 12,
										background: "#ef4444",
										border: "2px solid #fff",
										top: -6,
									}
								: { visibility: "hidden" }
						}
						type="target"
					/>
					<Handle
						id="yesEdge"
						position={Position.Bottom}
						style={
							data.hasOpenYesSource
								? {
										left: 20,
										visibility: "visible",
										width: 12,
										height: 12,
										background: "#ef4444",
										border: "2px solid #fff",
										bottom: -6,
									}
								: { left: 20, visibility: "hidden" }
						}
						type="source"
					/>
					<Handle
						id="noEdge"
						isConnectable={!data.waitUntil}
						position={Position.Bottom}
						style={
							data.hasOpenNoSource
								? {
										right: 12,
										left: "auto",
										visibility: "visible",
										width: 12,
										height: 12,
										background: "#ef4444",
										border: "2px solid #fff",
										bottom: -6,
									}
								: { right: 12, left: "auto", visibility: "hidden" }
						}
						type="source"
					/>
				</div>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={() => {
						data.deleteNode?.(props.id);
						setShowDeleteModal(false);
					}}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText="Delete"
					title="Delete node"
				>
					<div className="text-sm">Are you sure you want to delete this node?</div>
				</Modal>
			)}
		</div>
	);
};
